// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { DxWidgetUtils, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Third party imports
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Consant imports
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { Constants } from '../../../_constants/constants';
import { MessageInterval } from '../../../_constants/messages-intervals';

// Service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { SearchService } from '../../../search/_services/data-services/search.service';

// Model imports
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { KeyValueResultDto } from '../../../_models/key-value-result-dto';
import { OwnerSearchResultDto } from '../../../search/_models/owner-search-result-dto';
import { SearchParametersDto } from '../../../search/_models/search-parameters-dto';

@Component({
    selector: 'app-application-change-owner-editor',
    templateUrl: './application-change-owner-editor.component.html',
    styleUrls: ['./application-change-owner-editor.component.scss']
})
export class ApplicationChangeOwnerEditorComponent implements OnInit {
    @ViewChild('changeOwnerEditorPopup', { static: false }) changeOwnerEditorPopup: DxPopupComponent;
    @ViewChild('changeOwnerEditorGrid', { static: false }) changeOwnerEditorGrid: DxDataGridComponent;
    @ViewChild('messageBox', { static: false }) messageBox: MessageBoxComponent;

    @Input() visible: boolean;
    @Input() searchText: string;
    @Input() communityId: number;

    @Output() onOwnerSelect = new EventEmitter<OwnerSearchResultDto>();
    @Output() onClose = new EventEmitter<void>();

    defaultLabelLocation = Constants.defaultFormLabelLocation;

    loadingVisible = false;

    selectedOwnersType: string;
    ownersType: Array<KeyValueResultDto> = [];

    changeOwnerData: Array<OwnerSearchResultDto> = [];

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];
    selectedCommunityId: number;
    dateFormat: string;

    iconCheck = IconConstants.CHECK;
    iconSearch = IconConstants.SEARCH;
    iconClose = IconConstants.CLOSE;
    iconHelp = IconConstants.HELP;

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly searchService: SearchService,
        private readonly communityService: CommunityService,
        private readonly contextManagerService: ContextManagerService,
        private readonly ciraHelpService: CiraHelpService
    ) {}

    ngOnInit() {
        this.dateFormat = StringFormatConstants.dateFormat;
        this.ownersType = [
            {
                key: 'allOwners',
                displayText: 'All Owners',
                isDefault: false
            },
            {
                key: 'currentOwners',
                displayText: 'Current Owners',
                isDefault: true
            }
        ];

        this.selectedOwnersType = this.ownersType[1].key;

        const selectedContext = this.contextManagerService.getSelectedContext();

        if (Utils.notNullAndDefined(this.communityId)) {
            this.communityGridBoxValue = [this.communityId];
            this.selectedCommunityId = this.communityId;
        } else if (Utils.notNullAndDefined(selectedContext)) {
            // Check if editor is being accessed in community context, then pre select the community
            this.communityId = selectedContext.communityId;
            this.communityGridBoxValue = [selectedContext.communityId];
            this.selectedCommunityId = selectedContext.communityId;
        }

        this.getCommunityColumns();
    }

    onEditorShown(): void {
        DxWidgetUtils.instantRepaint(this.changeOwnerEditorPopup);

        this.onSearchClick();
        this.getCommunityList();
    }

    onEditorClose(): void {
        this.visible = false;
        this.onClose.emit();
    }

    onSearchTextChange(): void {
        if (Utils.isNullOrUndefined(this.searchText) || this.searchText.length < 3) {
            this.changeOwnerEditorGrid.instance.repaint();
        }
    }

    onSearchClick(): void {
        if (Utils.notNullAndDefined(this.searchText)) {
            if (this.searchText.length < 3) {
                this.messageBox.showErrorMessage('Error', 'Please provide at least 3 characters to search');
            } else {
                this.searchOwners();
            }
        }
    }

    onCommunitySelection(selectedRow: CommunitySelectorResultDto[]): void {
        if (Utils.notNullOrEmpty(selectedRow)) {
            this.selectedCommunityId = selectedRow[0].communityId;
        } else {
            this.selectedCommunityId = null;
        }
        this.onSearchClick();
    }

    onSelectOwner(data: OwnerSearchResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            this.onOwnerSelect.emit(data);
        }

        this.visible = false;
    }

    onHelpIconClick(): void {
        const helpURL = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.applicationHome);
        this.ciraHelpService.showHelp(helpURL);
    }

    private getCommunityList(): void {
        this.loadingVisible = true;
        this.communityService.getCommunities().subscribe(
            (result: CommunitySelectorResultDto[]) => {
                this.communityList = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private searchOwners(): void {
        let service: any;

        const baseParams: SearchParametersDto = new SearchParametersDto();
        baseParams.searchText = Utils.notNullAndDefined(this.searchText) ? this.searchText.trim().toLowerCase() : '';
        baseParams.communityId = Utils.notNullAndDefined(this.selectedCommunityId) ? this.selectedCommunityId : null;
        baseParams.clearCache = true;

        if (Utils.equalsIgnoreCase(this.selectedOwnersType, 'allOwners')) {
            service = this.searchService.searchAllOwners(baseParams);
        } else {
            service = this.searchService.searchCurrentOwners(baseParams);
        }

        this.loadingVisible = true;

        service.subscribe(
            (results: OwnerSearchResultDto[]) => {
                this.loadingVisible = false;
                this.changeOwnerData = results;
                if (this.changeOwnerData.length === 0) {
                    notify(
                        'There are no owners matching the search term',
                        'error',
                        MessageInterval.defaultErrorMsgInterval
                    );
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunityColumns(): void {
        this.communityColumns = [
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community',
                allowHeaderFiltering: false
            },
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Branch',
                allowHeaderFiltering: false
            },
            {
                dataField: 'managementCompanyName',
                dataType: 'string',
                caption: 'Management Company'
            }
        ];
    }
}
